import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { C } from "./shared";
import { getCoeExternalUrl } from "../../lib/coeExternalUrls";
import CoeLabCardLink from "../CoeLabCardLink";

export default function CoeLabsBand({ labs, labsError, onRetry }) {
  return (
    <section className="relative overflow-hidden text-white" style={{ background: C.navyDeep }} data-testid="landing-coe-band">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{ background: `radial-gradient(circle, ${C.green} 0%, transparent 70%)` }} aria-hidden="true" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-15 blur-3xl"
        style={{ background: `radial-gradient(circle, ${C.navy} 0%, transparent 70%)` }} aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7">
            <span className="font-mono text-xs tracking-[0.25em] uppercase" style={{ color: `${C.green}` }}>Backbone of the platform</span>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl mt-4 leading-[1.05]">
              Structured skill<br/>pathways that<br/>actually place.
            </h2>
          </div>
          <div className="md:col-span-5 md:pb-3">
            <p className="text-base md:text-lg text-white/70 leading-relaxed">
              Industry-grade labs across semiconductors, EVs, MedTech, AI hardware, smart manufacturing, agritech, electronics, cybersecurity, ADAS and IoT — finish one and recruiters see your verified badge on every application. Admin can keep extending the catalogue without writing code.
            </p>
            <Link to="/lms" className="mt-6 inline-flex items-center gap-2 font-semibold group" style={{ color: C.green }} data-testid="landing-explore-lms">
              Explore all pathways
              <span className="w-9 h-9 rounded-full border flex items-center justify-center group-hover:bg-white group-hover:text-ink transition" style={{ borderColor: `${C.green}60` }}>
                <ArrowUpRight size={16} />
              </span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="landing-labs-grid">
          {labs.length > 0 ? labs.map(l => {
            // Deep-link to the partner LMS (techcatalyst.world /
            // cidsaglobal.com) when a mapping exists for this CoE code;
            // otherwise keep the internal detail route as a fallback.
            const extUrl = getCoeExternalUrl(l.code);
            const cardClass = "group relative aspect-square rounded-sm overflow-hidden bg-white/5 border border-white/10 hover:border-white/30 transition-all";
            const CardInner = (
              <>
                {l.cover_image ? (
                  <>
                    <img src={l.cover_image} alt={l.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90 group-hover:opacity-100" />
                    <div className="absolute inset-0"
                      style={{ background: `linear-gradient(180deg, ${l.color}00 0%, ${l.color}10 40%, ${l.color}f0 100%)` }} />
                  </>
                ) : (
                  <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${l.color} 0%, ${l.color}80 100%)` }} />
                )}
                <div className="absolute inset-0 flex flex-col justify-between p-3 md:p-4">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-[10px] md:text-xs tracking-widest px-2 py-1 bg-white/95 rounded-sm font-bold" style={{ color: l.color }}>{l.code}</span>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <ArrowUpRight size={14} className="text-white" />
                    </span>
                  </div>
                  <div>
                    <div className="font-display font-bold text-sm md:text-base leading-tight text-white drop-shadow-md">
                      {l.name.split(/[—,&]/)[0].trim()}
                    </div>
                    <div className="font-mono text-[9px] md:text-[10px] tracking-widest uppercase text-white/80 mt-1">{l.category}</div>
                  </div>
                </div>
              </>
            );
            return extUrl ? (
              <CoeLabCardLink key={l.id}
                              labCode={l.code}
                              fallbackInternalHref={`/lms/labs/${l.id}`}
                              className={cardClass}
                              testid={`landing-lab-${l.code}`}>
                {CardInner}
              </CoeLabCardLink>
            ) : (
              <Link key={l.id} to={`/lms/labs/${l.id}`}
                    className={cardClass}
                    data-testid={`landing-lab-${l.code}`}>
                {CardInner}
              </Link>
            );
          }) : Array.from({ length: 10 }).map((_, idx) => (
            <div key={`lab-skeleton-${idx}`} className="aspect-square rounded-sm bg-white/5 border border-white/10 animate-pulse" />
          ))}
        </div>
        {labsError && (
          <div className="mt-6 max-w-md mx-auto text-center" data-testid="landing-labs-error">
            <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm px-5 py-4">
              <div className="text-white/85 text-sm">Couldn&apos;t load labs right now.</div>
              <button
                type="button"
                onClick={onRetry}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-ink text-xs font-bold uppercase tracking-wider hover:-translate-y-0.5 active:scale-95 transition-all"
                data-testid="landing-labs-retry"
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
